/**
 * Colar o texto pronto: o gerador lê e estrutura.
 *
 * Quase ninguém escreve o trabalho dentro de um editor de formatação — a pessoa
 * escreve no Word, no Google Docs ou no bloco de notas, e só depois quer a
 * formatação. Este módulo é essa porta de entrada (o passo "escreva ou cole seu
 * texto" de qualquer formatador acadêmico): recebe o texto cru e devolve o
 * documento já **estruturado** — seções com os níveis certos, parágrafos,
 * citações longas, resumo, palavras-chave e a lista de referências.
 *
 * Duas decisões governam tudo aqui:
 *
 * 1. **Nada é adivinhado em silêncio.** O que foi reconhecido sai na contagem e
 *    vem com avisos do que ficou de fora; o que não coube em lugar nenhum não é
 *    descartado sem a pessoa saber. Quando a leitura errar, ela corrige na tela —
 *    é mais rápido do que digitar tudo de novo.
 * 2. **O texto é da pessoa.** Nada é reescrito, resumido ou "melhorado": os
 *    títulos perdem a numeração que veio junto (o motor numera sozinho, pela
 *    norma) e a citação longa perde as aspas (a norma pede recuo, não aspas). O
 *    resto entra como está, com as palavras dela.
 *
 * A leitura é por **linhas**, que é como o texto sai do Word: um parágrafo por
 * linha. Quem cola de um PDF ou de um arquivo de texto com quebra de linha dura
 * tem as linhas juntadas de volta — a linha que termina sem pontuação e é
 * seguida de uma que começa em minúscula é continuação da mesma frase.
 */
import { citacaoLongaDeBlocos, paragrafosDeBlocos } from './blocos.js';
import { NIVEL_MAXIMO_DE_SECAO } from './tipos.js';
/* ------------------------------------------------------------------ */
/* Reconhecimento dos títulos                                          */
/* ------------------------------------------------------------------ */
/**
 * Os títulos que a norma conhece, sem número.
 *
 * São as peças que a ABNT nomeia: quando a linha é uma delas, o texto seguinte
 * não vira seção numerada — vira o **conteúdo daquela peça** (o resumo é o
 * campo resumo, a dedicatória é a dedicatória). Sem esta lista, colar um trabalho
 * inteiro criaria uma seção "RESUMO" no meio do texto.
 */
const PECAS_CONHECIDAS = {
    resumo: { destino: 'RESUMO' },
    abstract: { destino: 'ABSTRACT' },
    sumario: { destino: 'SUMARIO' },
    'sumário': { destino: 'SUMARIO' },
    referencias: { destino: 'REFERENCIAS' },
    'referências': { destino: 'REFERENCIAS' },
    'referencias bibliograficas': { destino: 'REFERENCIAS' },
    'referências bibliográficas': { destino: 'REFERENCIAS' },
    dedicatoria: { destino: 'dedicatoria' },
    'dedicatória': { destino: 'dedicatoria' },
    agradecimentos: { destino: 'agradecimentos' },
    epigrafe: { destino: 'epigrafe' },
    'epígrafe': { destino: 'epigrafe' },
    apresentacao: { destino: 'apresentacao' },
    'apresentação': { destino: 'apresentacao' },
    epilogo: { destino: 'epilogo' },
    'epílogo': { destino: 'epilogo' },
    'sobre o autor': { destino: 'sobreOAutor' },
    'lista de figuras': { destino: 'IGNORAR' },
    'lista de tabelas': { destino: 'IGNORAR' },
    'lista de abreviaturas': { destino: 'IGNORAR' },
    'lista de abreviaturas e siglas': { destino: 'IGNORAR' },
};
/** As palavras com que a ABNT rotula as palavras-chave (e as keywords). */
const ROTULOS_DE_PALAVRAS = /^(palavras[-\s]?chave|keywords?)\s*[:\-–]\s*/i;
/** Como o texto pode abrir um capítulo: "CAPÍTULO 3", "Capítulo III", "PARTE 2". */
const ROTULO_DE_CAPITULO = /^(cap[íi]tulo|parte)\s+([0-9]+|[ivxlcdm]+)\s*[-–—:.]?\s*/i;
/** Numeração progressiva no começo da linha: "2", "2.1", "2.1.3". */
const NUMERACAO = /^(\d+(?:\.\d+)*)[.)]?\s+(.*)$/;
/**
 * O texto está em caixa alta?
 *
 * Título de seção em maiúsculas é o que a norma pede para o primário, e é assim
 * que quase todo mundo escreve. A conta é feita sobre as letras, ignorando
 * números e pontuação, e exige ao menos uma letra.
 */
function emCaixaAlta(linha) {
    const letras = linha.replace(/[^\p{L}]/gu, '');
    return letras.length > 0 && letras === letras.toUpperCase();
}
/**
 * Esta linha é um título de seção?
 *
 * As pistas, em ordem de confiança:
 *
 * - **numeração** (`2.1 Metodologia`) — o nível é a própria profundidade do número;
 * - **rótulo de capítulo** (`CAPÍTULO 3 — O mar`) — livro;
 * - **peça conhecida da norma** (`RESUMO`, `REFERÊNCIAS`) — que não vira seção;
 * - **caixa alta curta** (`METODOLOGIA`) — o jeito clássico de escrever primário;
 * - **linha curta sem ponto final** e sem verbo aparente seria adivinhação demais,
 *   e por isso **não** entra: título sem nenhuma dessas marcas vira parágrafo, e a
 *   pessoa transforma em seção com um clique na tela.
 */
/** Linha de sumário ou de lista: tem pontos de ligação até o número da página. */
const LINHA_DE_SUMARIO = /\.{3,}\s*\d+\s*$/;
/**
 * Título em Markdown: `#`, `##`, `###` — e além disso, que o modelo não tem.
 *
 * Quem escreve em Markdown não pensa em "seção primária": pensa em `#`. A
 * profundidade do `#` é o nível, e é por isso que o Markdown entra aqui como uma
 * das pistas de título — a mais forte de todas, porque é escrita de propósito.
 */
/**
 * Título em Markdown: `#`, `##`… — **sem teto no número de `#`**.
 *
 * O nível é o número de `#`, e o que passa do sexto para no sexto (o editor oferece Título 1
 * a 6): quem cola de uma ferramenta que escreve `#######` recebe o título, e não uma linha de
 * texto perdida no meio do parágrafo.
 */
const TITULO_MARKDOWN = /^(#{1,})\s+(.*\S)\s*#*\s*$/;
/** Citação em Markdown: `> texto`. */
const CITACAO_MARKDOWN = /^\s*>\s*(.*\S)\s*$/;
/** Item de lista marcado: `- `, `* ` ou `+ `. */
const ITEM_MARCADO = /^\s*[-*+]\s+(\S.*)$/;
/** Item de lista numerado: `1. ` ou `1) `. */
const ITEM_NUMERADO = /^\s*\d+[.)]\s+(\S.*)$/;
/**
 * Quais linhas são **itens de lista numerada**.
 *
 * `1. Primeiro passo` e `1. INTRODUÇÃO` têm a mesma forma, e o que separa um do
 * outro é o que vem depois: a norma numera a seção (`1 INTRODUÇÃO`) e a lista vive
 * de itens em sequência. Então a numeração só vira lista quando ela **se repete** —
 * dois ou mais itens seguidos, com os números na ordem. Um "1. ALGO" sozinho
 * continua sendo título, que é o que os trabalhos de verdade escrevem.
 */
function itensNumerados(linhas) {
    const numeros = linhas.map((linha) => {
        const achado = linha.texto.trim().match(ITEM_NUMERADO);
        return achado ? indiceDoNumero(linha.texto.trim()) : null;
    });
    return numeros.map((numero, indice) => {
        if (numero === null)
            return false;
        const anterior = numeros[indice - 1] ?? null;
        const proximo = numeros[indice + 1] ?? null;
        return proximo === numero + 1 || anterior === numero - 1;
    });
}
/** O número do item ("2" em "2. Segundo passo"). */
function indiceDoNumero(linha) {
    const achado = linha.match(/^\s*(\d+)[.)]\s+/);
    return achado ? Number(achado[1]) : null;
}
function ehTitulo(linha) {
    const limpa = linha.trim();
    if (!limpa || limpa.length > 90)
        return false;
    /* `# Título`: escrito de propósito, então vem antes de qualquer outra pista. */
    if (TITULO_MARKDOWN.test(limpa))
        return true;
    /*
     * A linha do sumário colado tem a mesma cara de um título ("2 REVISÃO ..... 9"),
     * e tratá-la como título criaria seções fantasma a cada linha da lista. Os
     * pontos de ligação até a página são o que a denuncia.
     */
    if (LINHA_DE_SUMARIO.test(limpa))
        return false;
    if (ROTULO_DE_CAPITULO.test(limpa))
        return true;
    if (NUMERACAO.test(limpa))
        return true;
    if (PECAS_CONHECIDAS[limpa.toLowerCase().replace(/\s+/g, ' ')] && limpa.length <= 60)
        return true;
    // Caixa alta curta: até 12 palavras, sem terminar em ponto final.
    return emCaixaAlta(limpa) && !/[.;:]$/.test(limpa) && limpa.split(/\s+/).length <= 12;
}
/**
 * O título sem a numeração e sem o rótulo de capítulo.
 *
 * O motor numera por conta própria (1, 1.1, CAPÍTULO 2), então o número que veio
 * colado no texto sai daqui — senão o papel sairia com "2 2 METODOLOGIA".
 */
function semNumeracao(linha) {
    const limpa = linha.trim();
    /*
     * Markdown: o `#` manda em tudo o mais — quem escreve `## 3.1 Metodologia` está
     * dizendo o nível, e o número que veio junto sai (o motor numera por conta
     * própria, a partir da ordem das seções).
     */
    const emMarkdown = limpa.match(TITULO_MARKDOWN);
    if (emMarkdown) {
        const texto = emMarkdown[2].trim();
        const numerada = texto.match(NUMERACAO);
        return {
            titulo: numerada ? numerada[2].trim() : texto,
            nivel: Math.min(NIVEL_MAXIMO_DE_SECAO, emMarkdown[1].length),
            capitulo: false,
        };
    }
    const semCapitulo = limpa.replace(ROTULO_DE_CAPITULO, '');
    if (semCapitulo !== limpa) {
        return { titulo: semCapitulo.trim() || limpa, nivel: 1, capitulo: true };
    }
    const numerada = limpa.match(NUMERACAO);
    if (numerada) {
        const profundidade = numerada[1].split('.').length;
        const nivel = Math.min(NIVEL_MAXIMO_DE_SECAO, Math.max(1, profundidade));
        return { titulo: numerada[2].trim(), nivel, capitulo: false };
    }
    return { titulo: limpa, nivel: null, capitulo: false };
}
/* ------------------------------------------------------------------ */
/* Citação longa                                                       */
/* ------------------------------------------------------------------ */
/**
 * O bloco é uma citação longa?
 *
 * A norma manda citação de mais de três linhas com recuo de 4 cm e **sem
 * aspas**. No texto colado, o recuo vem de duas formas: o recuo de régua do Word
 * (que chega como quatro espaços ou um `Tab` no começo da linha) ou a citação
 * inteira entre aspas, ocupando várias linhas. As duas são reconhecidas — e as
 * aspas saem, porque quem põe o recuo é o motor.
 */
function ehCitacaoLonga(bloco) {
    if (bloco.length === 0)
        return false;
    /* `> citação`: no Markdown o recuo é escrito, não medido pelo olho. */
    if (bloco.every((linha) => CITACAO_MARKDOWN.test(linha)))
        return true;
    const recuado = bloco.every((linha) => /^(\t| {4,})/.test(linha));
    if (recuado)
        return true;
    const junto = bloco.join(' ').trim();
    const entreAspas = /^["“][\s\S]*["”]$/.test(junto);
    return entreAspas && junto.length > 200;
}
/** Tira o recuo, o `>` do Markdown e as aspas — o recuo quem dá é o motor. */
function limparCitacao(bloco) {
    return bloco
        .map((linha) => linha.replace(/^(\t| {4,})/, '').replace(CITACAO_MARKDOWN, '$1'))
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^["“]([\s\S]*)["”]$/, '$1')
        .trim();
}
/* ------------------------------------------------------------------ */
/* Leitura                                                             */
/* ------------------------------------------------------------------ */
/** O id de cada seção lida: estável, único e legível no documento salvo. */
function idDaSecao(indice, titulo) {
    const apelido = titulo
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 30);
    return `colado-${indice}${apelido ? `-${apelido}` : ''}`;
}
/**
 * Junta as linhas que foram quebradas pelo papel, não pelo autor.
 *
 * Colar de um PDF, de um `.txt` ou de um e-mail traz a quebra de linha dura: cada
 * ~80 caracteres, uma linha nova no meio da mesma frase. O sinal é claro — linha
 * que **não termina em pontuação** seguida de linha que **começa em minúscula** —,
 * e é o único caso em que o parágrafo é remontado. Parágrafo de verdade (que
 * termina em ponto) nunca é colado no seguinte, e linha separada por linha em
 * branco também não.
 */
function juntarLinhasQuebradas(linhas) {
    const saida = [];
    for (const linha of linhas) {
        const anterior = saida[saida.length - 1];
        const continua = anterior &&
            !linha.brancoAntes &&
            !/[.!?;:”")\]]$/.test(anterior.texto) &&
            /^[a-záéíóúâêôãõçàü]/.test(linha.texto);
        if (continua)
            anterior.texto = `${anterior.texto} ${linha.texto}`;
        else
            saida.push({ ...linha });
    }
    return saida;
}
/**
 * Junta as linhas de uma citação em Markdown num bloco só.
 *
 * No Markdown cada linha do bloco começa com `>`, e a quebra de linha ali é
 * escolha de quem escreveu (o texto corre, quem pagina é o leitor). Lidas uma a
 * uma, cada linha viraria uma citação separada — juntas, viram a citação que a
 * norma quer: um bloco recuado só.
 */
function agruparCitacoes(linhas) {
    const saida = [];
    for (const linha of linhas) {
        const anterior = saida[saida.length - 1];
        const continua = anterior && !linha.brancoAntes && CITACAO_MARKDOWN.test(anterior.texto) && CITACAO_MARKDOWN.test(linha.texto);
        if (continua) {
            /* Do `>` da linha seguinte só interessa o texto: o `>` do primeiro fica. */
            const texto = linha.texto.trim().replace(CITACAO_MARKDOWN, '$1');
            anterior.texto = `${anterior.texto.replace(/\s+$/, '')} ${texto}`;
        }
        else
            saida.push({ ...linha });
    }
    return saida;
}
/* ------------------------------------------------------------------ */
/* Tabelas em Markdown                                                 */
/* ------------------------------------------------------------------ */
/** Uma linha de tabela em Markdown: começa e termina com `|`. */
const LINHA_DE_TABELA = /^\s*\|(.+)\|\s*$/;
/** A linha de traços que separa o cabeçalho do corpo: `| --- | --- |`. */
const SEPARADOR_DE_TABELA = /^\s*\|(\s*:?-{2,}:?\s*\|)+\s*$/;
/** As células de uma linha: `| a | b |` → `['a', 'b']`. */
function celulasDaLinha(linha) {
    const achado = linha.match(LINHA_DE_TABELA);
    if (!achado)
        return [];
    return achado[1]
        .split(/(?<!\\)\|/)
        .map((celula) => celula.replace(/\\\|/g, '|').trim());
}
/**
 * As tabelas do texto, linha por linha.
 *
 * Uma tabela é reconhecida pelo **separador**: `| a | b |` seguido de `| --- | --- |`.
 * É o formato que o próprio gerador escreve no `.md`, e é o único jeito de não
 * confundir uma linha com pipes (que aparece em texto corrido) com um quadro.
 * A legenda e a fonte viajam como linhas vizinhas (`Legenda:` / `Fonte:`), porque a
 * sintaxe de pipes não tem onde guardá-las.
 */
function tabelasDoTexto(linhas) {
    const marcadas = new Map();
    for (let i = 0; i < linhas.length; i += 1) {
        const linha = linhas[i].texto.trim();
        if (!LINHA_DE_TABELA.test(linha))
            continue;
        if (!SEPARADOR_DE_TABELA.test((linhas[i + 1]?.texto ?? '').trim()))
            continue;
        const cabecalho = celulasDaLinha(linha);
        const corpo = [];
        let fim = i + 1;
        for (let j = i + 2; j < linhas.length; j += 1) {
            const proxima = linhas[j].texto.trim();
            if (!LINHA_DE_TABELA.test(proxima))
                break;
            corpo.push(celulasDaLinha(proxima));
            fim = j;
        }
        const anterior = linhas[i - 1]?.texto.trim() ?? '';
        const legenda = anterior.match(/^legenda:\s*(.+)$/i)?.[1]?.trim();
        const depois = linhas[fim + 1]?.texto.trim() ?? '';
        const fonte = depois.match(/^fonte:\s*(.+)$/i)?.[1]?.trim();
        const bloco = {
            id: `tabela-${i}`,
            tipo: 'TABELA',
            legenda: legenda ?? '',
            fonte: fonte ?? '',
            linhas: [cabecalho, ...corpo],
        };
        /* A primeira linha carrega o bloco; as outras (e a legenda e a fonte) são puladas. */
        marcadas.set(i, bloco);
        if (legenda)
            marcadas.set(i - 1, null);
        for (let j = i + 1; j <= fim; j += 1)
            marcadas.set(j, null);
        if (fonte)
            marcadas.set(fim + 1, null);
    }
    return marcadas;
}
/**
 * Os blocos da seção que a leitura está montando.
 *
 * A leitura nasceu gravando `paragrafos`; agora ela grava **blocos**, e o primeiro
 * bloco de uma seção nasce do que já estava lá — nada se perde no meio do caminho.
 * `paragrafos` e `citacaoLonga` são reescritos no fim, como espelho.
 */
function blocosDaSecaoEmConstrucao(secao) {
    if (!secao.blocos) {
        secao.blocos = secao.paragrafos.map((texto, indice) => ({
            id: `${secao.id}-p${indice}`,
            tipo: 'PARAGRAFO',
            texto,
        }));
    }
    return secao.blocos;
}
/**
 * Lê o texto colado e devolve o documento estruturado.
 *
 * Aceita as duas formas em que o texto chega: o **Word** (títulos em caixa alta
 * ou numerados, recuo de régua na citação) e o **Markdown** (`# título`, `>
 * citação`, `- item`). Markdown é o caminho de ida **e** de volta —
 * `documentoEmMarkdown` despeja o documento nesse formato, e ele volta para cá
 * igual.
 *
 * `modelo` só decide o que é capítulo: no livro, título de primeiro nível é
 * capítulo (e o motor escreve "1."); no trabalho acadêmico, é a seção
 * numerada da norma.
 */
export function lerTextoColado(texto, modelo = 'TRABALHO_ACADEMICO') {
    const cru = texto
        .replace(/\r\n?/g, '\n')
        .replace(/\u00a0/g, ' ')
        .replace(/[\u200b-\u200d\ufeff]/g, '')
        .split('\n')
        .map((linha, indice, todas) => ({
        texto: linha.replace(/\s+$/, ''),
        brancoAntes: indice > 0 && !todas[indice - 1].trim(),
    }));
    const linhas = agruparCitacoes(juntarLinhasQuebradas(cru.filter((linha) => linha.texto.trim())));
    const secoes = [];
    const referencias = [];
    const campos = {};
    const avisos = [];
    let resumo = '';
    let abstract = '';
    let palavrasChave = [];
    let keywords = [];
    let contagemParagrafos = 0;
    let contagemCitacoes = 0;
    let contagemItens = 0;
    let contagemTabelas = 0;
    /** Onde os parágrafos estão caindo agora. */
    let destino = 'SECAO';
    let campoAtual = null;
    /** Blocos acumulados no destino que não é seção (resumo, campo, referência). */
    let acumulado = [];
    const fecharAcumulado = () => {
        if (acumulado.length === 0)
            return;
        const junto = acumulado.join(' ').replace(/\s+/g, ' ').trim();
        acumulado = [];
        if (destino === 'RESUMO')
            resumo = resumo ? `${resumo}\n\n${junto}` : junto;
        else if (destino === 'ABSTRACT')
            abstract = abstract ? `${abstract}\n\n${junto}` : junto;
        else if (destino === 'CAMPO' && campoAtual)
            campos[campoAtual] = junto;
    };
    /* Quais linhas são itens de lista numerada (ver `itensNumerados`). */
    const numerados = itensNumerados(linhas);
    /* E quais são tabela (ver `tabelasDoTexto`): a tabela é lida de uma vez só. */
    const tabelas = tabelasDoTexto(linhas);
    for (const [posicao, linha] of linhas.entries()) {
        /*
         * `cru` guarda o começo da linha (é dele que sai o recuo da citação);
         * `conteudo` é a linha sem espaço nas pontas, que é o que entra no texto.
         */
        const cru = linha.texto.replace(/\s+$/, '');
        const conteudo = cru.trim();
        if (!conteudo)
            continue;
        /*
         * Item de lista em Markdown (`- `, `* `, `+ `): o marcador sai e o item entra
         * como parágrafo. O texto corrido da norma não tem marcador — quem lista é a
         * alínea, que o modelo ainda não desenha — e imprimir o hífen no meio da
         * página seria pior do que o aviso no fim.
         */
        /*
         * Tabela: a primeira linha do quadro vira o bloco (com a legenda e a fonte que
         * estavam em volta), e as outras linhas do quadro são puladas — elas já estão
         * dentro do bloco.
         */
        if (tabelas.has(posicao)) {
            const tabela = tabelas.get(posicao);
            const secao = secoes[secoes.length - 1];
            if (tabela && secao) {
                blocosDaSecaoEmConstrucao(secao).push({ ...tabela, id: `${secao.id}-t${posicao}` });
                contagemTabelas += 1;
            }
            continue;
        }
        const numerado = numerados[posicao] ? conteudo.match(ITEM_NUMERADO) : null;
        const item = conteudo.match(ITEM_MARCADO) ?? numerado;
        /** O texto sem o marcador de lista: é o que entra no bloco. */
        const semMarcador = item ? item[1].trim() : conteudo;
        /*
         * Citação longa vem **antes** do título: o recuo de régua é a marca mais
         * forte que existe no texto colado, e um trecho recuado em caixa alta seria
         * lido como título se a ordem fosse a outra.
         */
        if (destino === 'SECAO' && ehCitacaoLonga([cru])) {
            const secao = secoes[secoes.length - 1];
            if (secao) {
                /*
                 * A citação entra **onde ela estava** no texto colado, e não no fim da seção:
                 * quem escreveu pôs o trecho ali, e é ali que ele sai impresso.
                 */
                blocosDaSecaoEmConstrucao(secao).push({
                    id: `${secao.id}-c${contagemCitacoes}`,
                    tipo: 'CITACAO',
                    texto: limparCitacao([cru]),
                });
                contagemCitacoes += 1;
                continue;
            }
        }
        /*
         * Item de lista **não** passa pelo título: `1. Primeiro passo` tem exatamente a
         * forma da seção numerada da norma (`1. INTRODUÇÃO`), e quem decide é a leitura —
         * só a numeração em sequência vira lista (ver `itensNumerados`).
         */
        if (!item && ehTitulo(conteudo)) {
            /*
             * A peça da norma é procurada pelo título **limpo**: `## REFERÊNCIAS` é a
             * mesma peça que `REFERÊNCIAS`, e procurar com os `#` na frente não acharia
             * nada — a lista viraria uma seção chamada "REFERÊNCIAS" no meio do texto.
             */
            const { titulo, nivel, capitulo } = semNumeracao(conteudo);
            const chave = titulo.toLowerCase().replace(/\s+/g, ' ');
            const peca = PECAS_CONHECIDAS[chave] ?? PECAS_CONHECIDAS[conteudo.toLowerCase().replace(/\s+/g, ' ')];
            // A lista de palavras-chave fecha o resumo e passa a valer como tal.
            fecharAcumulado();
            if (peca) {
                if (peca.destino === 'RESUMO')
                    destino = 'RESUMO';
                else if (peca.destino === 'ABSTRACT')
                    destino = 'ABSTRACT';
                else if (peca.destino === 'REFERENCIAS')
                    destino = 'REFERENCIAS';
                else if (peca.destino === 'SUMARIO') {
                    destino = 'IGNORAR';
                    avisos.push('O sumário que veio no texto foi ignorado: o gerador monta o sumário sozinho, com a numeração das seções.');
                }
                else if (peca.destino === 'IGNORAR') {
                    destino = 'IGNORAR';
                    avisos.push(`A lista "${titulo}" foi ignorada: ela é montada pelo gerador a partir do que você inserir.`);
                }
                else {
                    destino = 'CAMPO';
                    campoAtual = peca.destino;
                    if (campos[campoAtual]) {
                        campos[campoAtual] = `${campos[campoAtual]}\n\n${conteudo}`;
                    }
                }
                continue;
            }
            destino = 'SECAO';
            secoes.push({
                id: idDaSecao(secoes.length + 1, titulo),
                titulo,
                nivel: nivel ?? 1,
                paragrafos: [],
                ...(capitulo || (modelo === 'LIVRO' && (nivel ?? 1) === 1) ? { novaPagina: true } : {}),
            });
            continue;
        }
        // Palavras-chave: a linha que a norma usa para fechar o resumo.
        const rotulo = conteudo.match(ROTULOS_DE_PALAVRAS);
        if (rotulo && (destino === 'RESUMO' || destino === 'ABSTRACT')) {
            const palavras = conteudo
                .slice(rotulo[0].length)
                .split(/[;,]/)
                .map((palavra) => palavra.trim().replace(/\.$/, ''))
                .filter(Boolean);
            if (destino === 'RESUMO')
                palavrasChave = palavras;
            else
                keywords = palavras;
            continue;
        }
        if (destino === 'REFERENCIAS') {
            /*
             * Cada linha da lista é uma referência. Ela entra **como foi escrita**
             * (tipo `LIVRE`): adivinhar os campos de uma referência pronta — autor,
             * título, cidade, editora, ano — erraria em silêncio em metade dos casos, e
             * a lista é justamente o que a banca confere.
             */
            referencias.push({
                id: `colada-${referencias.length + 1}`,
                tipo: 'LIVRE',
                autores: [],
                titulo: '',
                local: '',
                ano: '',
                texto: conteudo,
            });
            continue;
        }
        if (destino === 'IGNORAR')
            continue;
        if (destino === 'SECAO') {
            const secao = secoes[secoes.length - 1];
            if (!secao) {
                // Texto antes do primeiro título: vira a primeira seção, sem nome.
                secoes.push({
                    id: idDaSecao(1, 'texto'),
                    titulo: modelo === 'LIVRO' ? 'Capítulo 1' : 'Introdução',
                    nivel: 1,
                    paragrafos: [],
                    blocos: [{ id: 'secao-1-p0', tipo: 'PARAGRAFO', texto: conteudo }],
                });
                contagemParagrafos += 1;
                avisos.push(`O texto começa sem título. Os primeiros parágrafos entraram em "${modelo === 'LIVRO' ? 'Capítulo 1' : 'Introdução'}" — renomeie se for outro o nome.`);
                continue;
            }
            const blocos = blocosDaSecaoEmConstrucao(secao);
            const ultimo = blocos[blocos.length - 1];
            /*
             * Item de lista (`- item`, `1. item`) entra como **bloco de lista**, e os itens
             * seguidos se juntam num bloco só. Antes o marcador era jogado fora e o item
             * virava parágrafo, com um aviso no fim: o texto colado do Word perdia a lista,
             * que é justamente o que a pessoa tinha escrito.
             */
            if (item && ultimo?.tipo === 'LISTA') {
                ultimo.itens = [...(ultimo.itens ?? []), item[1].trim()];
                contagemItens += 1;
                continue;
            }
            if (item) {
                blocos.push({
                    id: `${secao.id}-l${blocos.length}`,
                    tipo: 'LISTA',
                    lista: numerado ? 'NUMERADA' : 'MARCADA',
                    itens: [item[1].trim()],
                });
                contagemItens += 1;
                continue;
            }
            blocos.push({ id: `${secao.id}-p${blocos.length}`, tipo: 'PARAGRAFO', texto: conteudo });
            contagemParagrafos += 1;
            continue;
        }
        acumulado.push(semMarcador);
    }
    fecharAcumulado();
    if (referencias.length > 0) {
        avisos.push(`${referencias.length} referência(s) entraram como você escreveu. Se quiser que o gerador formate cada uma na NBR 6023, cadastre os campos na aba Referências.`);
    }
    if (secoes.length === 0 && referencias.length === 0 && !resumo && Object.keys(campos).length === 0) {
        avisos.push('Não foi reconhecido nada neste texto. Confira se ele foi colado por inteiro.');
    }
    /*
     * O espelho: `paragrafos` e `citacaoLonga` continuam sendo gravados, agora a partir
     * dos blocos. É o que mantém o documento legível para quem só conhece os campos
     * antigos — o `.md`, a contagem de texto — e o que faz um texto sem listas sair
     * exatamente como saía antes.
     */
    for (const secao of secoes) {
        if (!secao.blocos)
            continue;
        secao.paragrafos = paragrafosDeBlocos(secao.blocos);
        const citacao = citacaoLongaDeBlocos(secao.blocos);
        if (citacao)
            secao.citacaoLonga = citacao;
    }
    const palavras = texto.trim() ? texto.trim().split(/\s+/).length : 0;
    return {
        secoes,
        referencias,
        campos,
        resumo,
        palavrasChave,
        abstract,
        keywords,
        contagem: {
            secoes: secoes.length,
            paragrafos: contagemParagrafos,
            citacoesLongas: contagemCitacoes,
            itens: contagemItens,
            tabelas: contagemTabelas,
            referencias: referencias.length,
            palavras,
        },
        avisos,
    };
}
/**
 * Aplica o que foi lido sobre o documento.
 *
 * `substituir` decide o que acontece com o que já existe: o padrão é trocar
 * (é o caso de quem está trazendo o texto pronto), e desmarcado o que veio é
 * **acrescentado ao fim** — útil para trazer um capítulo para um documento que já
 * tem outros. O que a leitura não reconheceu (referências, resumo, campos) é
 * mesclado sem apagar nada.
 */
export function aplicarTextoEstruturado(documento, lido, opcoes = {}) {
    const substituir = opcoes.substituir ?? true;
    /*
     * As seções lidas não podem colidir de id com as que já existem (o React usa o id
     * como chave e o motor, para achar a seção): ao acrescentar, cada id ganha o
     * sufixo da vez. Os **blocos** são renomeados junto — o id do bloco nasce do id da
     * seção, e dois blocos com o mesmo id em seções diferentes confundiriam o editor
     * na hora de mover ou apagar um deles.
     */
    const existentes = new Set(documento.secoes.map((secao) => secao.id));
    const lidas = lido.secoes.map((secao) => {
        if (!existentes.has(secao.id)) {
            existentes.add(secao.id);
            return secao;
        }
        let sufixo = 2;
        while (existentes.has(`${secao.id}-${sufixo}`))
            sufixo += 1;
        const id = `${secao.id}-${sufixo}`;
        existentes.add(id);
        return {
            ...secao,
            id,
            ...(secao.blocos
                ? { blocos: secao.blocos.map((bloco, indice) => ({ ...bloco, id: `${id}-b${indice}` })) }
                : {}),
        };
    });
    const referenciasExistentes = new Set(documento.referencias.map((referencia) => referencia.id));
    const referenciasLidas = lido.referencias.map((referencia) => {
        if (!referenciasExistentes.has(referencia.id)) {
            referenciasExistentes.add(referencia.id);
            return referencia;
        }
        let sufixo = 2;
        while (referenciasExistentes.has(`${referencia.id}-${sufixo}`))
            sufixo += 1;
        referenciasExistentes.add(`${referencia.id}-${sufixo}`);
        return { ...referencia, id: `${referencia.id}-${sufixo}` };
    });
    /*
     * Quem manda em cada campo: ao **trocar** o texto, o que veio colado; ao
     * **acrescentar**, o que já estava — quem colou o texto para somar um capítulo
     * não pode perder o resumo que já havia escrito. Em nenhum dos dois casos um
     * campo que a leitura não reconheceu apaga o que existia.
     */
    const escolher = (colado, atual) => (substituir ? colado || atual : atual || colado);
    const escolherLista = (colada, atual) => substituir ? (colada.length > 0 ? colada : atual) : atual.length > 0 ? atual : colada;
    const campos = Object.fromEntries(Object.entries(lido.campos).filter(([chave, valor]) => {
        if (!valor?.trim())
            return false;
        return substituir || !documento[chave]?.trim();
    }));
    return {
        ...documento,
        secoes: substituir ? lidas : [...documento.secoes, ...lidas],
        referencias: [...documento.referencias, ...referenciasLidas],
        resumo: escolher(lido.resumo, documento.resumo),
        palavrasChave: escolherLista(lido.palavrasChave, documento.palavrasChave),
        abstract: escolher(lido.abstract, documento.abstract),
        keywords: escolherLista(lido.keywords, documento.keywords),
        ...campos,
    };
}
//# sourceMappingURL=importar.js.map